import { Input } from "./Input";
import React, { useState, useEffect, useMemo } from "react";
import { Autocomplete, TextField, Checkbox } from "@mui/material";
import Chip from "@mui/material/Chip";
import CustomSelect from "./select";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@material-ui/core/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useHistory } from "react-router-dom";
import { useUserAuthentication } from "../../../../../utils/utils";
import { config } from "../../../../../config/config";

export const JsonPopover = (props) => {
  const {
    value,
    currentJsonValueMetadata,
    admin_data,
    setMetadata,
    updateAlert,
    updateAdminInputs,
    outlinedCNCButton,
    containedButton,
    isJsonDialogOpen,
    setIsJsonDialogOpen,
  } = props;
  const [data, setData] = useState(value || {});
  const [errorMessage, setErrorMessage] = useState("");
  const [rowHasError, setRowHasError] = useState(false);

  const validation = () => {};

  const jsonHasChanged = useMemo(() => {
    const originalValue =
      admin_data?.usecase_metadata?.[currentJsonValueMetadata?.name];
    return JSON.stringify(originalValue) !== JSON.stringify(data);
  }, [admin_data?.usecase_metadata, currentJsonValueMetadata?.name, data]);

  const handleOnInputChange = (e, fieldName) => {
    const editedValue = e.target.value;

    // Get the original value from admin_data to check its type
    const originalValue = admin_data?.usecase_metadata?.[fieldName];

    let valueToSave = editedValue;

    // Determine if original value is a complex type (object or array)
    const isComplexType =
      originalValue &&
      typeof originalValue === "object" &&
      (Array.isArray(originalValue) || !Array.isArray(originalValue));

    // If the original value was a complex type (object or array), try to parse back
    if (isComplexType) {
      try {
        // Try to parse as JSON
        const parsedValue = JSON.parse(editedValue);

        // Validate that the parsed value is of the same type as original
        const originalIsArray = Array.isArray(originalValue);
        const parsedIsArray = Array.isArray(parsedValue);

        // Only save if types match
        if (originalIsArray === parsedIsArray) {
          valueToSave = parsedValue;
        }
        // If types don't match, keep as string (user is still typing)
      } catch (err) {
        // If parsing fails, keep it as string (user is still typing)
        valueToSave = editedValue;
      }
    }

    setData(valueToSave);
  };

  const formatInputValue = (value) => {
    if (value === null || value === undefined) return "";
    if (Array.isArray(value)) return JSON.stringify(value, null, 2);
    if (typeof value === "object") return JSON.stringify(value, null, 2);
    return String(value);
  };

  const handleDialogClose = () => {
    setIsJsonDialogOpen(false);
  };

  const validateEditedJson = () => {
    const originalValue =
      admin_data?.usecase_metadata?.[currentJsonValueMetadata?.name];
    const originalIsComplex =
      originalValue && typeof originalValue === "object";

    // Work with a string representation for lightweight checks
    const strValue = typeof data === "string" ? data : JSON.stringify(data);
    const errors = [];

    const count = (s, ch) => (s.match(new RegExp(`\\${ch}`, "g")) || []).length;

    const openBraces = count(strValue, "{");
    const closeBraces = count(strValue, "}");
    if (openBraces !== closeBraces)
      errors.push("Mismatched braces: open/close '{' '}' counts differ.");

    const openBrackets = count(strValue, "[");
    const closeBrackets = count(strValue, "]");
    if (openBrackets !== closeBrackets)
      errors.push("Mismatched brackets: open/close '[' ']' counts differ.");

    // Count unescaped double quotes
    let quoteCount = 0;
    for (let i = 0; i < strValue.length; i++) {
      if (strValue[i] === '"' && (i === 0 || strValue[i - 1] !== "\\"))
        quoteCount++;
    }
    if (quoteCount % 2 !== 0)
      errors.push("Double quotes are not properly closed.");

    // Trailing commas like { "a": 1, } or [1,]
    if (/,(\s*[}\]])/.test(strValue))
      errors.push("Trailing comma before closing brace/bracket.");

    // Try to parse to get precise JSON.parse errors if present
    try {
      const parsed = typeof data === "string" ? JSON.parse(data) : data;

      // If original was array vs object, ensure types match
      if (originalIsComplex) {
        const originalIsArray = Array.isArray(originalValue);
        const parsedIsArray = Array.isArray(parsed);
        if (originalIsArray !== parsedIsArray) {
          errors.push(
            `Type mismatch: expected ${originalIsArray ? "array" : "object"} but got ${
              parsedIsArray ? "array" : typeof parsed
            }.`,
          );
        }
      }
    } catch (err) {
      const msg = err && err.message ? err.message : "Invalid JSON.";
      if (/Unexpected end of JSON input/.test(msg)) {
        errors.push(
          "JSON ended unexpectedly: missing closing bracket/brace or incomplete value.",
        );
      } else if (/Unexpected token/.test(msg)) {
        const m = msg.match(/Unexpected token (.+) in JSON at position (\d+)/);
        if (m) {
          const token = m[1];
          if (token === ",")
            errors.push("Unexpected comma or missing value before comma.");
          else if (token === ":")
            errors.push("Unexpected colon or missing key/value around colon.");
          else
            errors.push(
              `Unexpected token ${token} in JSON at position ${m[2]}.`,
            );
        } else {
          errors.push(msg);
        }
      } else {
        errors.push(msg);
      }
    }

    if (errors.length) {
      const message = errors.join(" ");
      setErrorMessage(message);
      setRowHasError(true);
      updateAlert({
        open: true,
        message,
        severity: "error",
        closeFlag: true,
      });
      return false;
    }

    // clear previous errors
    setErrorMessage("");
    setRowHasError(false);
    return true;
  };

  const handleSave = () => {
    setErrorMessage("");
    setRowHasError(false);
    if (!jsonHasChanged) {
      updateAlert({
        open: true,
        message: "No changes have been made.",
        severity: "info",
        closeFlag: true,
      });
      return;
    }

    // Run validation before saving
    const isValid = validateEditedJson();
    if (!isValid) return;

    // If data is a string (user edited JSON text), parse it back to object/array before saving
    const originalValue =
      admin_data?.usecase_metadata?.[currentJsonValueMetadata?.name];
    let valueToSave = data;
    const originalIsComplex =
      originalValue && typeof originalValue === "object";
    if (originalIsComplex && typeof data === "string") {
      try {
        valueToSave = JSON.parse(data);
      } catch (err) {
        // Parsing should succeed because we already validated; fallback keep as string
        valueToSave = data;
      }
    }

    setMetadata((prev) => ({
      ...prev,
      [currentJsonValueMetadata.name]: valueToSave,
    }));
    handleDialogClose();
  };

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth={"md"}
        open={isJsonDialogOpen}
        onClose={handleDialogClose}
      >
        <DialogTitle>Edit Json</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            <div
              style={{
                maxHeight: "74vh",
                overflowY: "auto",
                paddingRight: "15px",
              }}
            >
              <div style={{ flex: 1, width: "100%" }}>
                <Input
                  customStyle={{
                    border: "3px solid #a3a3a3",
                    borderRadius: "8px",
                    padding: "8px",
                  }}
                  showLineNumber={true}
                  errorMessage={errorMessage}
                  rowSize={10}
                  rowHasError={rowHasError}
                  columnDataArr={[]}
                  classes={{}}
                  type={currentJsonValueMetadata.type}
                  name={currentJsonValueMetadata.name}
                  onChange={(e) =>
                    handleOnInputChange(e, currentJsonValueMetadata.name)
                  }
                  readonly={!currentJsonValueMetadata.editable}
                  value={formatInputValue(data || {})}
                  item={currentJsonValueMetadata.name}
                  childHasError={(bool) => {}}
                  error={""}
                  validation={validation}
                />
              </div>
            </div>
            {rowHasError && (
              <p
                style={{
                  color: "red",
                  fontSize: "14px",
                  border: "1px solid red",
                  borderRadius: "4px",
                  padding: "5px",
                  marginTop: "20px",
                }}
              >
                {errorMessage}
              </p>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            disableFocusRipple
            variant="outlined"
            style={outlinedCNCButton}
            onClick={handleDialogClose}
          >
            Cancel
          </Button>
          {currentJsonValueMetadata?.editable && (
            <Button
              disableFocusRipple
              style={{
                ...containedButton,
                cursor: "pointer",
              }}
              onClick={handleSave}
            >
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
